import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import type { MathConfig, Position, ResolvedTheme, SymbolWinEffect } from '../types';
import { alphaOf, toPixiColor } from '../util/color';
import type { ReelSet } from './reels';
import type { TextureStore } from './textures';
import type { Timeline } from './tween';

/**
 * Symbol-level effects: dimming non-winners, win animations, land glows.
 * `layer` must be added to the stage at the ReelSet origin (below the LineLayer).
 */
export class SymbolFx {
  readonly layer = new Container();
  private readonly dimGfx = new Graphics();
  private readonly glows = new Container();
  /** Winning symbols are re-drawn here, above the masked reel columns, so scale/float never clips at the cell edge. */
  private readonly lifted = new Container();
  /** Shared gold frame around each winning cell (assets.winFrame), above the symbols; its stars may overflow the cell. */
  private readonly frames = new Container();
  private landStops: Array<() => void> = [];
  private theme: ResolvedTheme;
  private readonly math: MathConfig;
  private readonly timeline: Timeline;
  private readonly textures: TextureStore;
  private stops: Array<() => void> = [];
  private lastGrid: string[][] | null = null;

  constructor(o: { theme: ResolvedTheme; math: MathConfig; timeline: Timeline; textures: TextureStore }) {
    this.theme = o.theme;
    this.math = o.math;
    this.timeline = o.timeline;
    this.textures = o.textures;
    this.layer.addChild(this.glows, this.dimGfx, this.lifted, this.frames);
    this.layer.eventMode = 'none';
  }

  /** Clone a reel sprite onto the unmasked layer at its cell centre and hide the original while the clone lives. */
  private lift(reels: ReelSet, reel: number, row: number): { clone: Sprite; original: Sprite; x: number; y: number; sx: number; sy: number; dw: number; dh: number } {
    const original = reels.reel(reel).symbolSprites[row];
    const c = reels.cellPosition(reel, row);
    const clone = new Sprite(original.texture);
    clone.anchor.set(0.5);
    clone.scale.copyFrom(original.scale);
    clone.position.set(c.x, c.y);
    clone.eventMode = 'none';
    this.lifted.addChild(clone);
    original.renderable = false;
    // Display size in reel px: frame textures may have another pixel size than the static image and must not change it.
    return { clone, original, x: c.x, y: c.y, sx: clone.scale.x, sy: clone.scale.y, dw: original.width, dh: original.height };
  }

  private drop(l: { clone: Sprite; original: Sprite }): void {
    l.original.renderable = true;
    if (!l.clone.destroyed) l.clone.destroy();
  }

  setTheme(theme: ResolvedTheme): void {
    this.theme = theme;
  }

  /** Draw the dim overlay over every cell that is not in `winning`. */
  dim(reels: ReelSet, winning: Position[]): void {
    const keep = new Set(winning.map(([r, c]) => `${r}:${c}`));
    const { w, h } = reels.symbolSize;
    const color = toPixiColor(this.theme.palette.symbolDim);
    const alpha = alphaOf(this.theme.palette.symbolDim);
    this.dimGfx.clear();
    for (let reel = 0; reel < this.math.reels; reel++) {
      for (let row = 0; row < this.math.rows; row++) {
        if (keep.has(`${reel}:${row}`)) continue;
        const c = reels.cellPosition(reel, row);
        this.dimGfx.roundRect(c.x - w / 2, c.y - h / 2, w, h, 8).fill({ color, alpha });
      }
    }
  }

  /** Loop a win effect on the given cells; returns a stop function that restores the sprites. */
  animate(reels: ReelSet, positions: Position[], effect: SymbolWinEffect, loopMs: number): () => void {
    if (effect === 'none' || positions.length === 0) return () => {};
    const targets = positions.map(([reel, row]) => {
      const l = this.lift(reels, reel, row);
      return { ...l, sprite: l.clone, reel, row, id: this.lastGrid?.[reel]?.[row] ?? '' };
    });
    const { w, h } = reels.symbolSize;
    const glowColor = toPixiColor(this.theme.palette.winNormal);
    const glowShapes: Graphics[] = [];
    const frameSets = new Map<Sprite, { frames: Texture[]; fps: number; last: number }>();

    if (effect === 'glow') {
      for (const t of targets) {
        const c = reels.cellPosition(t.reel, t.row);
        const g = new Graphics().roundRect(c.x - w / 2 - 4, c.y - h / 2 - 4, w + 8, h + 8, 12).fill({ color: glowColor, alpha: 0.5 });
        this.glows.addChild(g);
        glowShapes.push(g);
      }
    }
    // Frames are used only when every sheet of the theme has arrived, so all winning symbols animate the same way.
    const useFrames = effect === 'frames' && this.textures.winSheetsReady(this.theme, this.math);
    if (useFrames) {
      for (const t of targets) {
        const frames = this.textures.frames(t.id);
        if (!frames) continue;
        frameSets.set(t.sprite, { frames, fps: this.textures.framesFps(t.id, this.theme.assets.symbols[t.id]?.fps ?? 12), last: 0 });
        // First frame right away, so a slow render tick never shows the static image before the clip starts.
        t.sprite.texture = frames[0];
        t.sprite.width = t.dw;
        t.sprite.height = t.dh;
      }
    }
    // Frame sets play at their own rate (a 32-frame sheet at 16 fps loops every 2 s); other effects use the theme's loop.
    const first = frameSets.values().next().value;
    const period = first ? (first.frames.length / first.fps) * 1000 : loopMs;
    // Gold frame around every winning cell: the frame line's rectangle (asset `rect`) maps onto the cell grown by
    // `margin`, the rest of the clip (stars) overflows; fades in over `appearMs`, then loops at its own rate.
    const spec = this.theme.assets.winFrame;
    const frameFx = this.theme.animations.effects.winFrame;
    const frameTex = spec && frameFx.enabled ? this.textures.winFrameFrames() : null;
    const frameSprites: Sprite[] = [];
    const frameStart = performance.now();
    const frameLoopMs = frameTex ? (frameTex.length / this.textures.winFrameFps()) * 1000 : 1;
    let frameIdx = 0;
    if (spec && frameTex) {
      const grow = 1 + frameFx.margin;
      const W = (w * grow) / spec.rect.w;
      const H = (h * grow) / spec.rect.h;
      for (const t of targets) {
        const sp = new Sprite(frameTex[0]);
        sp.width = W;
        sp.height = H;
        sp.position.set(t.x - (spec.rect.x + spec.rect.w / 2) * W, t.y - (spec.rect.y + spec.rect.h / 2) * H);
        sp.alpha = 0;
        sp.eventMode = 'none';
        this.frames.addChild(sp);
        frameSprites.push(sp);
      }
      void this.timeline.tween({
        duration: Math.max(1, frameFx.appearMs),
        update: (k) => {
          for (const sp of frameSprites) if (!sp.destroyed) sp.alpha = k;
        },
      });
    }
    let stopped = false;
    const stop = this.timeline.loop({
      period,
      update: (p) => {
        // The loop can be stepped once more in the tick that restored the sprites (line cycle redraw): skip dead clones.
        if (stopped) return;
        const wave = Math.sin(p * Math.PI * 2);
        for (const t of targets) {
          if (t.sprite.destroyed) continue;
          // Until the sheets are in (background download after start) every symbol floats instead.
          const mode: SymbolWinEffect = effect === 'frames' && !frameSets.has(t.sprite) ? 'float' : effect;
          if (mode === 'pulse') {
            const k = 1 + 0.12 * (0.5 + 0.5 * wave);
            t.sprite.scale.set(t.sx * k, t.sy * k);
          } else if (mode === 'float') {
            // Lucky Fruit win-float: scale 1.08 -> 1.12 with a -3px lift at the peak
            const u = 0.5 + 0.5 * wave;
            const k = 1.08 + 0.04 * u;
            t.sprite.scale.set(t.sx * k, t.sy * k);
            t.sprite.y = t.y - h * 0.045 * u;
          } else if (mode === 'bounce') {
            t.sprite.y = t.y - Math.abs(wave) * h * 0.12;
          } else if (mode === 'frames') {
            const set = frameSets.get(t.sprite)!;
            const idx = Math.floor(p * set.frames.length) % set.frames.length;
            if (idx !== set.last) {
              set.last = idx;
              t.sprite.texture = set.frames[idx];
              t.sprite.width = t.dw;
              t.sprite.height = t.dh;
            }
          }
        }
        for (const g of glowShapes) g.alpha = 0.45 + 0.55 * (0.5 + 0.5 * wave);
        if (frameTex && frameSprites.length) {
          const fi = Math.floor(((performance.now() - frameStart) / frameLoopMs) * frameTex.length) % frameTex.length;
          if (fi !== frameIdx) {
            frameIdx = fi;
            for (const sp of frameSprites) if (!sp.destroyed) sp.texture = frameTex[fi];
          }
        }
      },
    });
    const restore = () => {
      stopped = true;
      stop();
      for (const t of targets) this.drop(t);
      for (const g of glowShapes) g.destroy();
      for (const sp of frameSprites) if (!sp.destroyed) sp.destroy();
      this.stops = this.stops.filter((s) => s !== restore);
    };
    this.stops.push(restore);
    return restore;
  }

  /** The presenter tells us the current grid so frame animations can find their symbol id. */
  setGrid(grid: string[][]): void {
    this.lastGrid = grid;
  }

  /** One-shot glow when a scatter or wild lands. */
  land(reels: ReelSet, position: Position, kind: 'scatter' | 'wild'): void {
    const mode = kind === 'scatter' ? this.theme.animations.effects.scatterLand : this.theme.animations.effects.wildLand;
    if (mode === 'none') return;
    const [reel, row] = position;
    const c = reels.cellPosition(reel, row);
    const { w, h } = reels.symbolSize;
    const color = toPixiColor(kind === 'scatter' ? this.theme.palette.scatterGlow : this.theme.palette.wildGlow);
    const g = new Graphics().roundRect(c.x - w / 2 - 6, c.y - h / 2 - 6, w + 12, h + 12, 14).fill({ color, alpha: 0.6 });
    this.glows.addChild(g);
    // Pulse on an unmasked clone so the enlarged symbol is not cut by the column mask.
    const l = mode === 'pulse' ? this.lift(reels, reel, row) : null;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (l) this.drop(l);
      if (!g.destroyed) g.destroy();
      this.landStops = this.landStops.filter((f) => f !== finish);
    };
    this.landStops.push(finish);
    void this.timeline
      .tween({
        duration: 450,
        update: (t) => {
          if (done) return;
          g.alpha = 0.7 * (1 - t);
          if (l) {
            const k = 1 + 0.18 * Math.sin(t * Math.PI);
            l.clone.scale.set(l.sx * k, l.sy * k);
          }
        },
      })
      .then(finish, finish);
  }

  clear(): void {
    for (const s of [...this.stops]) s();
    this.stops = [];
    for (const f of [...this.landStops]) f();
    this.landStops = [];
    this.dimGfx.clear();
    this.glows.removeChildren().forEach((c) => c.destroy());
    this.frames.removeChildren().forEach((c) => c.destroy());
  }
}
